import {
  API_WEBSOCKET_ENDPOINT,
  AUDIO_SAMPLE_RATE,
  AUDIO_CHANNELS,
  AUDIO_BUFFER_SIZE,
  WEBSOCKET_TIMEOUT_MS,
} from "../config";

export interface AudioRecordingOptions {
  onDataAvailable?: (blob: Blob) => void;
  onTranscriptReceived?: (transcript: string) => void;
  onError?: (error: Error) => void;
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private audioContext: AudioContext | null = null;
  private websocket: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private isRecording = false;
  private processor: ScriptProcessorNode | null = null;
  private analyser: AnalyserNode | null = null;
  private isCleaningUp = false;

  constructor(private accessToken: string) {}

  async startRecording(options: AudioRecordingOptions = {}): Promise<void> {
    console.log("Starting recording, current state:", {
      isRecording: this.isRecording,
      hasMediaRecorder: !!this.mediaRecorder,
      hasWebSocket: !!this.websocket,
      hasStream: !!this.stream,
      hasAudioContext: !!this.audioContext,
    });

    // If already recording, stop first
    if (this.isRecording) {
      console.warn("Already recording, stopping first");
      await this.stopRecording();
    }

    // Make absolutely sure we're starting with a clean state
    await this.fullCleanup();

    try {
      console.log("Initializing recording with clean state");

      // Get audio stream with specific constraints
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: AUDIO_SAMPLE_RATE,
          channelCount: AUDIO_CHANNELS,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Create audio context with specific sample rate
      this.audioContext = new AudioContext({
        sampleRate: AUDIO_SAMPLE_RATE,
      });

      // Connect to WebSocket first before starting audio processing
      console.log("Connecting to WebSocket...");
      await this.connectWebSocket(options);
      console.log("WebSocket connected successfully");

      // Create source from stream
      const source = this.audioContext.createMediaStreamSource(this.stream);

      // Create analyser
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;

      // Create script processor for raw PCM data
      this.processor = this.audioContext.createScriptProcessor(
        AUDIO_BUFFER_SIZE,
        1,
        1
      );

      // Connect the nodes
      source.connect(this.analyser);
      this.analyser.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      // Process audio data
      this.processor.onaudioprocess = (e) => {
        if (
          !this.isRecording ||
          !this.websocket ||
          this.websocket.readyState !== WebSocket.OPEN
        ) {
          return;
        }

        // Get raw PCM data from input channel
        const inputData = e.inputBuffer.getChannelData(0);

        // Convert float32 audio data to int16
        const int16Data = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          // Convert float32 [-1.0, 1.0] to int16 [-32768, 32767]
          int16Data[i] = Math.max(
            -32768,
            Math.min(32767, Math.floor(inputData[i] * 32767))
          );
        }

        // Send the int16 data as binary message
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          try {
            this.websocket.send(int16Data.buffer);
          } catch (error) {
            console.error("Error sending audio data to WebSocket:", error);
            if (options.onError) {
              options.onError(new Error("Failed to send audio data to server"));
            }
          }
        }
      };

      // Set up MediaRecorder for saving the audio locally
      // Try to use common audio formats in order of preference
      const mimeTypes = [
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
        "audio/wav",
        "audio/mpeg",
      ];

      // Find the first supported MIME type
      let mimeType = "";
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      // Create MediaRecorder with the supported MIME type
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: mimeType || "",
      });
      console.log(`Using MIME type: ${this.mediaRecorder.mimeType}`);

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);

          if (options.onDataAvailable) {
            options.onDataAvailable(event.data);
          }
        }
      };

      // Set recording flag BEFORE starting MediaRecorder
      this.isRecording = true;

      // Start recording
      this.mediaRecorder.start(100); // Collect data every 100ms for local storage
      console.log("MediaRecorder started successfully");
    } catch (error) {
      // Clean up any resources if initialization failed
      console.error("Error starting recording:", error);
      await this.fullCleanup();

      if (options.onError) {
        options.onError(
          error instanceof Error ? error : new Error(String(error))
        );
      }
      throw error;
    }
  }

  async stopRecording(): Promise<Blob> {
    console.log("Stopping recording, current state:", {
      isRecording: this.isRecording,
      mediaRecorderState: this.mediaRecorder
        ? this.mediaRecorder.state
        : "null",
      hasWebSocket: !!this.websocket,
      webSocketState: this.websocket ? this.websocket.readyState : "null",
      audioChunksCount: this.audioChunks.length,
    });

    if (!this.isRecording) {
      console.warn("Not recording according to isRecording flag");
      return new Blob([], { type: "audio/webm" });
    }

    // Make a copy of audio chunks before cleanup
    const chunks = [...this.audioChunks];
    const mimeType = this.mediaRecorder?.mimeType || "audio/webm";

    if (!this.mediaRecorder) {
      console.warn("MediaRecorder is null, cannot stop recording normally");
      await this.fullCleanup();
      return new Blob(chunks, { type: mimeType });
    }

    if (this.mediaRecorder.state === "inactive") {
      console.warn("MediaRecorder is already inactive");
      await this.fullCleanup();
      return new Blob(chunks, { type: mimeType });
    }

    return new Promise<Blob>((resolve) => {
      // Set a timeout to prevent hanging
      const stopTimeout = setTimeout(async () => {
        console.warn("MediaRecorder stop timeout, forcing cleanup");
        await this.fullCleanup();
        resolve(new Blob(chunks, { type: mimeType }));
      }, 3000);

      // Set up the onstop handler
      if (this.mediaRecorder) {
        this.mediaRecorder.onstop = async () => {
          clearTimeout(stopTimeout);
          await this.fullCleanup();
          resolve(new Blob(chunks, { type: mimeType }));
        };

        // Set up error handler
        this.mediaRecorder.onerror = async (event) => {
          clearTimeout(stopTimeout);
          console.error("MediaRecorder error during stop:", event);
          await this.fullCleanup();
          resolve(new Blob(chunks, { type: mimeType }));
        };

        try {
          // Try to stop the recorder
          console.log("Calling stop() on MediaRecorder");
          this.mediaRecorder.stop();
        } catch (error) {
          clearTimeout(stopTimeout);
          console.error("Error stopping MediaRecorder:", error);
          this.fullCleanup();
          resolve(new Blob(chunks, { type: mimeType }));
        }
      } else {
        clearTimeout(stopTimeout);
        this.fullCleanup();
        resolve(new Blob([], { type: "audio/webm" }));
      }
    });
  }

  // New method for complete cleanup with better error handling
  async fullCleanup(): Promise<void> {
    // Prevent multiple simultaneous cleanups
    if (this.isCleaningUp) {
      console.log("Cleanup already in progress, waiting...");
      // Wait for current cleanup to finish
      await new Promise((resolve) => setTimeout(resolve, 500));
      return;
    }

    this.isCleaningUp = true;
    console.log("Starting full cleanup of audio recorder resources");

    try {
      // 1. Stop MediaRecorder if it's still recording
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        try {
          console.log("Stopping active MediaRecorder");
          this.mediaRecorder.stop();
        } catch (e) {
          console.error("Error stopping MediaRecorder:", e);
        }
      }

      // 2. Clear MediaRecorder event handlers
      if (this.mediaRecorder) {
        this.mediaRecorder.ondataavailable = null;
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.onerror = null;
        this.mediaRecorder = null;
      }

      // 3. Close WebSocket connection
      if (this.websocket) {
        // Remove all event listeners first
        this.websocket.onopen = null;
        this.websocket.onmessage = null;
        this.websocket.onerror = null;
        this.websocket.onclose = null;

        // Send close frame
        if (this.websocket.readyState === WebSocket.OPEN) {
          try {
            console.log("Closing WebSocket connection");
            this.websocket.close(1000, "cleanup");
          } catch (e) {
            console.error("Error closing WebSocket:", e);
          }
        }

        this.websocket = null;
      }

      // 4. Disconnect and clean up audio processing nodes
      if (this.processor) {
        try {
          this.processor.disconnect();
          this.processor.onaudioprocess = null;
        } catch (e) {
          console.error("Error disconnecting processor:", e);
        }
        this.processor = null;
      }

      if (this.analyser) {
        try {
          this.analyser.disconnect();
        } catch (e) {
          console.error("Error disconnecting analyser:", e);
        }
        this.analyser = null;
      }

      // 5. Close audio context
      if (this.audioContext) {
        try {
          if (this.audioContext.state !== "closed") {
            await this.audioContext.close();
          }
        } catch (e) {
          console.error("Error closing audio context:", e);
        }
        this.audioContext = null;
      }

      // 6. Stop all tracks in the stream
      if (this.stream) {
        try {
          this.stream.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping media tracks:", e);
        }
        this.stream = null;
      }

      // 7. Reset recording state
      this.isRecording = false;
      this.audioChunks = [];
    } catch (e) {
      console.error("Error during full cleanup:", e);
    } finally {
      this.isCleaningUp = false;
      console.log("Full cleanup completed");
    }
  }

  // Legacy method for backward compatibility
  public cleanupResources(): void {
    this.fullCleanup().catch((e) =>
      console.error("Error in cleanupResources:", e)
    );
  }

  private async connectWebSocket(
    options: AudioRecordingOptions
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Always use the absolute URL to the backend API regardless of deployment domain
      const wsUrl = `${API_WEBSOCKET_ENDPOINT}?access_token=Bearer ${encodeURIComponent(
        this.accessToken
      )}`;

      console.log("Connecting to WebSocket with URL:", wsUrl);

      // Close any existing connection
      if (this.websocket) {
        console.log(
          "Closing existing WebSocket connection before creating a new one"
        );
        try {
          // Remove all event listeners
          this.websocket.onopen = null;
          this.websocket.onmessage = null;
          this.websocket.onerror = null;
          this.websocket.onclose = null;

          if (this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.close(1000, "Creating new connection");
          }
        } catch (e) {
          console.error("Error closing existing WebSocket:", e);
        }
        this.websocket = null;
      }

      // Create new WebSocket connection
      try {
        this.websocket = new WebSocket(wsUrl);
        console.log(
          "WebSocket instance created, readyState:",
          this.websocket.readyState
        );
      } catch (error) {
        console.error("Error creating WebSocket:", error);
        reject(new Error("Failed to create WebSocket connection"));
        return;
      }

      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.websocket && this.websocket.readyState !== WebSocket.OPEN) {
          console.error(
            "WebSocket connection timeout after",
            WEBSOCKET_TIMEOUT_MS,
            "ms"
          );
          const error = new Error("WebSocket connection timeout");
          if (options.onError) {
            options.onError(error);
          }
          reject(error);
        }
      }, WEBSOCKET_TIMEOUT_MS);

      this.websocket.onopen = () => {
        console.log("WebSocket connection established successfully");
        clearTimeout(connectionTimeout);
        resolve();
      };

      this.websocket.onmessage = (event) => {
        if (options.onTranscriptReceived) {
          options.onTranscriptReceived(event.data);
        }
      };

      this.websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
        clearTimeout(connectionTimeout);
        if (options.onError) {
          options.onError(new Error("WebSocket connection error"));
        }
        reject(new Error("WebSocket connection error"));
      };

      this.websocket.onclose = (event) => {
        console.log(
          `WebSocket connection closed: ${event.code} ${event.reason}`
        );

        // If we're still recording when the connection closes unexpectedly,
        // notify the error handler
        if (this.isRecording && options.onError) {
          options.onError(
            new Error(`WebSocket connection closed unexpectedly: ${event.code}`)
          );
        }
      };
    });
  }

  isCurrentlyRecording(): boolean {
    return (
      this.isRecording &&
      !!this.mediaRecorder &&
      this.mediaRecorder.state === "recording"
    );
  }

  async getRecordedAudio(): Promise<Blob> {
    // If we have audio chunks, create a blob from them
    if (this.audioChunks.length > 0) {
      // Use the MIME type from the MediaRecorder if available
      const mimeType = this.mediaRecorder?.mimeType || "audio/webm";
      return new Blob(this.audioChunks, { type: mimeType });
    }

    // Return an empty blob if no audio chunks are available
    return new Blob([], { type: "audio/webm" });
  }
}
